import { create } from 'zustand';
import { TritonModelInfo } from '@/mock/gpu-instance';

export interface ModelVersion {
  version: string;
  backend: TritonModelInfo['backend'];
  maxBatchSize: number;
  configPbtxt: string;
  files: string[];
  uploadedAt: number;
  status: 'ready' | 'loading' | 'unavailable' | 'error';
  isActive: boolean;
  metrics: {
    inferenceCount: number;
    avgLatencyMs: number;
    p99LatencyMs: number;
    queueTimeMs: number;
  };
}

export interface UploadedModel {
  name: string;
  version: string;
  backend: TritonModelInfo['backend'];
  maxBatchSize: number;
  configPbtxt: string;
  files: string[]; // file names
  uploadedAt: number;
}

// Model with version history
export interface ModelWithVersions {
  name: string;
  versions: ModelVersion[];
  activeVersion: string;
  createdAt: number;
  updatedAt: number;
}

interface TritonModelStore {
  // Models added via upload (keyed by instanceId)
  additionalModels: Record<string, UploadedModel[]>;
  // Models marked as deleted (keyed by instanceId, then model name)
  deletedModelNames: Record<string, Set<string>>;
  // Version history for models (keyed by instanceId, then model name)
  modelVersionHistory: Record<string, Record<string, ModelWithVersions>>;

  // Actions
  addModel: (instanceId: string, model: UploadedModel) => void;
  deleteModel: (instanceId: string, modelName: string) => void;
  deleteModelVersion: (instanceId: string, modelName: string, version: string) => void;
  setActiveVersion: (instanceId: string, modelName: string, version: string) => void;
  resetStore: () => void;
  resetInstance: (instanceId: string) => void;

  // Getters
  getAdditionalModels: (instanceId: string) => UploadedModel[];
  isModelDeleted: (instanceId: string, modelName: string) => boolean;
  getModelVersionHistory: (instanceId: string, modelName: string) => ModelWithVersions | null;
  getAllModelVersions: (instanceId: string) => Record<string, ModelWithVersions>;
  getNextVersion: (instanceId: string, modelName: string) => string;
}

export const useTritonModelStore = create<TritonModelStore>((set, get) => ({
  additionalModels: {},
  deletedModelNames: {},
  modelVersionHistory: {},

  addModel: (instanceId: string, model: UploadedModel) => {
    set((state) => {
      const existingModels = state.additionalModels[instanceId] || [];

      // Update version history
      const instanceHistory = state.modelVersionHistory[instanceId] || {};
      const existingModelHistory = instanceHistory[model.name];

      const newVersion: ModelVersion = {
        version: model.version,
        backend: model.backend,
        maxBatchSize: model.maxBatchSize,
        configPbtxt: model.configPbtxt,
        files: model.files,
        uploadedAt: model.uploadedAt,
        status: 'ready',
        isActive: !existingModelHistory, // First version is active by default
        metrics: {
          inferenceCount: 0,
          avgLatencyMs: 0,
          p99LatencyMs: 0,
          queueTimeMs: 0,
        },
      };

      let updatedModelHistory: ModelWithVersions;

      if (existingModelHistory) {
        // Add new version to existing model
        const updatedVersions = existingModelHistory.versions.map(v => ({
          ...v,
          isActive: false, // Deactivate previous versions
        }));
        updatedVersions.push({ ...newVersion, isActive: true });

        updatedModelHistory = {
          ...existingModelHistory,
          versions: updatedVersions,
          activeVersion: model.version,
          updatedAt: Date.now(),
        };
      } else {
        // Create new model entry
        updatedModelHistory = {
          name: model.name,
          versions: [newVersion],
          activeVersion: model.version,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
      }

      return {
        additionalModels: {
          ...state.additionalModels,
          [instanceId]: [...existingModels, model],
        },
        modelVersionHistory: {
          ...state.modelVersionHistory,
          [instanceId]: {
            ...instanceHistory,
            [model.name]: updatedModelHistory,
          },
        },
      };
    });
  },

  deleteModel: (instanceId: string, modelName: string) => {
    set((state) => {
      const existingDeleted = state.deletedModelNames[instanceId] || new Set<string>();
      const newDeleted = new Set(existingDeleted);
      newDeleted.add(modelName);

      // Also remove from additionalModels if it was uploaded
      const existingModels = state.additionalModels[instanceId] || [];
      const filteredModels = existingModels.filter((m) => m.name !== modelName);

      // Remove from version history
      const instanceHistory = { ...state.modelVersionHistory[instanceId] };
      delete instanceHistory[modelName];

      return {
        deletedModelNames: {
          ...state.deletedModelNames,
          [instanceId]: newDeleted,
        },
        additionalModels: {
          ...state.additionalModels,
          [instanceId]: filteredModels,
        },
        modelVersionHistory: {
          ...state.modelVersionHistory,
          [instanceId]: instanceHistory,
        },
      };
    });
  },

  deleteModelVersion: (instanceId: string, modelName: string, version: string) => {
    set((state) => {
      const instanceHistory = state.modelVersionHistory[instanceId];
      if (!instanceHistory || !instanceHistory[modelName]) return state;

      const modelHistory = instanceHistory[modelName];
      const filteredVersions = modelHistory.versions.filter(v => v.version !== version);

      // If no versions left, delete the entire model
      if (filteredVersions.length === 0) {
        const { [modelName]: _, ...restHistory } = instanceHistory;
        const existingDeleted = state.deletedModelNames[instanceId] || new Set<string>();
        const newDeleted = new Set(existingDeleted);
        newDeleted.add(modelName);

        return {
          ...state,
          deletedModelNames: {
            ...state.deletedModelNames,
            [instanceId]: newDeleted,
          },
          modelVersionHistory: {
            ...state.modelVersionHistory,
            [instanceId]: restHistory,
          },
        };
      }

      // If active version was deleted, activate the latest version
      let newActiveVersion = modelHistory.activeVersion;
      if (version === modelHistory.activeVersion) {
        const latestVersion = filteredVersions.reduce((latest, v) =>
          v.uploadedAt > latest.uploadedAt ? v : latest
        );
        newActiveVersion = latestVersion.version;
        filteredVersions.forEach(v => {
          v.isActive = v.version === newActiveVersion;
        });
      }

      return {
        ...state,
        modelVersionHistory: {
          ...state.modelVersionHistory,
          [instanceId]: {
            ...instanceHistory,
            [modelName]: {
              ...modelHistory,
              versions: filteredVersions,
              activeVersion: newActiveVersion,
              updatedAt: Date.now(),
            },
          },
        },
      };
    });
  },

  setActiveVersion: (instanceId: string, modelName: string, version: string) => {
    set((state) => {
      const instanceHistory = state.modelVersionHistory[instanceId];
      if (!instanceHistory || !instanceHistory[modelName]) return state;

      const modelHistory = instanceHistory[modelName];
      const updatedVersions = modelHistory.versions.map(v => ({
        ...v,
        isActive: v.version === version,
      }));

      return {
        ...state,
        modelVersionHistory: {
          ...state.modelVersionHistory,
          [instanceId]: {
            ...instanceHistory,
            [modelName]: {
              ...modelHistory,
              versions: updatedVersions,
              activeVersion: version,
              updatedAt: Date.now(),
            },
          },
        },
      };
    });
  },

  resetStore: () => {
    set({ additionalModels: {}, deletedModelNames: {}, modelVersionHistory: {} });
  },

  resetInstance: (instanceId: string) => {
    set((state) => {
      const { [instanceId]: _, ...restAdditional } = state.additionalModels;
      const { [instanceId]: __, ...restDeleted } = state.deletedModelNames;
      const { [instanceId]: ___, ...restHistory } = state.modelVersionHistory;
      return {
        additionalModels: restAdditional,
        deletedModelNames: restDeleted,
        modelVersionHistory: restHistory,
      };
    });
  },

  getAdditionalModels: (instanceId: string) => {
    return get().additionalModels[instanceId] || [];
  },

  isModelDeleted: (instanceId: string, modelName: string) => {
    const deleted = get().deletedModelNames[instanceId];
    return deleted ? deleted.has(modelName) : false;
  },

  getModelVersionHistory: (instanceId: string, modelName: string) => {
    const instanceHistory = get().modelVersionHistory[instanceId];
    if (!instanceHistory) return null;
    return instanceHistory[modelName] || null;
  },

  getAllModelVersions: (instanceId: string) => {
    return get().modelVersionHistory[instanceId] || {};
  },

  getNextVersion: (instanceId: string, modelName: string) => {
    const history = get().getModelVersionHistory(instanceId, modelName);
    if (!history || history.versions.length === 0) {
      return '1';
    }
    const maxVersion = Math.max(...history.versions.map(v => parseInt(v.version) || 0));
    return String(maxVersion + 1);
  },
}));

// Helper function to convert uploaded model to TritonModelInfo
export const uploadedModelToTritonModelInfo = (
  model: UploadedModel,
  instanceId: string
): TritonModelInfo => ({
  name: model.name,
  version: model.version,
  backend: model.backend,
  status: 'ready',
  endpoint: `https://api.float16.cloud/v2/gpu/${instanceId}/models/${model.name}/infer`,
  inputFormat: 'varies',
  outputFormat: 'varies',
  instances: 1,
  dynamicBatching: model.maxBatchSize > 1,
  maxBatchSize: model.maxBatchSize,
  metrics: {
    inferenceCount: 0,
    avgLatencyMs: 0,
    p99LatencyMs: 0,
    queueTimeMs: 0,
  },
});

// Helper function to convert ModelVersion to TritonModelInfo
export const modelVersionToTritonModelInfo = (
  modelName: string,
  version: ModelVersion,
  instanceId: string
): TritonModelInfo => ({
  name: modelName,
  version: version.version,
  backend: version.backend,
  status: version.status,
  endpoint: `https://api.float16.cloud/v2/gpu/${instanceId}/models/${modelName}/versions/${version.version}/infer`,
  inputFormat: 'varies',
  outputFormat: 'varies',
  instances: 1,
  dynamicBatching: version.maxBatchSize > 1,
  maxBatchSize: version.maxBatchSize,
  metrics: version.metrics,
});
